import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import SelectDropdown from 'src/Components/GlobalComponents/SelectDropdown';
import httpRequest from 'src/helpers/httpRequest';
import { allMonths, allYears, currentYear } from 'src/lib/constans';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { dummySalesData } from 'src/lib/DummmyData';

interface IMonthlyData {
    _id: { year: number; month: number };
    totalAmount: number;
};
interface ISaleResponse {
    diagnosticOrders: { monthlyData: IMonthlyData[]; };
    pharmaOrders: { monthlyData: IMonthlyData[]; };
    homeCareServices: { monthlyData: IMonthlyData[]; };
    consultations: { monthlyData: IMonthlyData[]; };
};

const OverAllStatsGraph: React.FC<{ title: string; type: "BarChart" | "LineChart" }> = ({ title, type }) => {
    const [salesStates, setSalesStates] = useState<{
        loader: boolean;
        year: number;
        diagnosticOrders: IMonthlyData[];
        pharmaOrders: IMonthlyData[];
        homeCareServices: IMonthlyData[];
        consultations: IMonthlyData[];
    }>({
        loader: false,
        year: currentYear,
        diagnosticOrders: [],
        pharmaOrders: [],
        homeCareServices: [],
        consultations: [],
    });

    const fromatedSalesData = (): any => {
        const formattedData = allMonths.map(({ month, name }) => {
            const tests = salesStates?.diagnosticOrders?.filter(v => v?._id?.year === salesStates.year)?.find(v => v?._id?.month === month);
            const pharmacy = salesStates?.pharmaOrders?.filter(v => v?._id?.year === salesStates.year)?.find(v => v?._id?.month === month);
            const homecare = salesStates?.homeCareServices?.filter(v => v?._id?.year === salesStates.year)?.find(v => v?._id?.month === month);
            const consultation = salesStates?.consultations?.filter(v => v?._id?.year === salesStates.year)?.find(v => v?._id?.month === month);
            
            // Cap values at 100,000 for graph display
            const capValue = (value: number) => Math.min(value, 100000);
            
            return {
                name: name,
                tests: tests ? capValue(Number(tests?.totalAmount.toFixed(2))) : 0,
                pharmacy: pharmacy ? capValue(Number(pharmacy?.totalAmount.toFixed(2))) : 0,
                homecare: homecare ? capValue(Number(homecare?.totalAmount.toFixed(2))) : 0,
                consultation: consultation ? capValue(Number(consultation?.totalAmount.toFixed(2))) : 0,
                // Keep original values for tooltip
                originalTests: tests ? Number(tests?.totalAmount.toFixed(2)) : 0,
                originalPharmacy: pharmacy ? Number(pharmacy?.totalAmount.toFixed(2)) : 0,
                originalHomecare: homecare ? Number(homecare?.totalAmount.toFixed(2)) : 0,
                originalConsultation: consultation ? Number(consultation?.totalAmount.toFixed(2)) : 0,
            };
        });
        return formattedData;
    };

    // Fixed Y-axis range: 0 to 100,000
    const yAxisDomain = [0, 100000];
    const yAxisTicks = [0, 25000, 50000, 75000, 100000];
    const fetchSalesStates = async () => {
        setSalesStates(prev => ({ ...prev, loader: true }));
        const { res, err } = await httpRequest<ISaleResponse>({ path: `/pharma-order/admin/statistics/sales?year=${salesStates?.year}` });
        // let res = dummySalesData;
        if (res) {
            setSalesStates(prev => ({
                ...prev,
                diagnosticOrders: res?.diagnosticOrders?.monthlyData || [],
                pharmaOrders: res?.pharmaOrders?.monthlyData || [],
                homeCareServices: res?.homeCareServices?.monthlyData || [],
                consultations: res?.consultations?.monthlyData || [],
            }));
        } else {
            toast(err, { type: "error" });
        }
        setSalesStates(prev => ({ ...prev, loader: false }));
    };
    useEffect(() => {
        fetchSalesStates();
    }, [salesStates?.year]);
    return (
        <div>
            <h2 className="font-Poppins-SemiBold text-[#232323] font-size-20px mb-2">{title}</h2>
            <div className='user-graph-inner'>
                <div className='flex justify-between items-center mb-2 lg:mb-[0.7vw]'>
                    <div className='flex flex-wrap gap-4'>
                        <span className="flex items-center gap-2">
                            <div className='bg-[#00a297] w-[10px] h-[10px] rounded-full'></div>
                            <span className="font-Poppins-Regular text-sm text-gray-700">Consultation</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <div className='bg-[#00443f] w-[10px] h-[10px] rounded-full'></div>
                            <span className="font-Poppins-Regular text-sm text-gray-700">Tests</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <div className='bg-[#00bb8e] w-[10px] h-[10px] rounded-full'></div>
                            <span className="font-Poppins-Regular text-sm text-gray-700">Pharmacy</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <div className='bg-[#1b8e50] w-[10px] h-[10px] rounded-full'></div>
                            <span className="font-Poppins-Regular text-sm text-gray-700">Homecare</span>
                        </span>
                    </div>
                    <div className='select-container-graph-user-manag'>
                        <div className='select-container-graph select-dropdown-container-graps w-auto'>
                            <SelectDropdown
                                options={allYears.map(v => ({ label: String(v), value: v }))}
                                isMulti={false}
                                data={allYears.map(v => ({ label: String(v), value: v }))?.filter(v => v.value === salesStates.year)}
                                setData={(e) => setSalesStates(prev => ({ ...prev, year: e[0] ? e[0]?.value : "" }))}
                                checkboxShow={false}
                                isLabel={false}
                                isRequired={true}
                                clearIcon={false}
                            />
                        </div>
                    </div>
                </div>
                {salesStates?.loader &&
                    <div className="text-center">
                        <CircularProgress size={20} style={{ color: "#004540" }} />
                    </div>
                }
                <div className="user-graph-container">
                    <ResponsiveContainer width="100%" height="100%">
                        {type === "BarChart" ?
                            <BarChart
                                data={fromatedSalesData()}
                                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                                barCategoryGap="15%"
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    className='revenue-axis' 
                                    dataKey="name" 
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                    tickLine={{ stroke: '#e0e0e0' }}
                                />
                                <YAxis 
                                    className='revenue-axis' 
                                    domain={yAxisDomain}
                                    ticks={yAxisTicks}
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                    tickLine={{ stroke: '#e0e0e0' }}
                                    tickFormatter={(value) => value.toLocaleString()}
                                />
                                <Tooltip 
                                    wrapperClassName='revenue-axis'
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                    formatter={(value, name, props) => {
                                        const nameStr = String(name);
                                        const originalValue = props.payload[`original${nameStr.charAt(0).toUpperCase() + nameStr.slice(1)}`];
                                        return [originalValue.toLocaleString(), name];
                                    }}
                                />
                                <Bar dataKey="consultation" fill="#00a297" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="tests" fill="#00443f" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="pharmacy" fill="#00bb8e" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="homecare" fill="#1b8e50" radius={[4, 4, 0, 0]} />
                            </BarChart>
                            :
                            <LineChart
                                data={fromatedSalesData()}
                                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    className='revenue-axis' 
                                    dataKey="name" 
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                    tickLine={{ stroke: '#e0e0e0' }}
                                />
                                <YAxis 
                                    className='revenue-axis' 
                                    domain={yAxisDomain}
                                    ticks={yAxisTicks}
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                    tickLine={{ stroke: '#e0e0e0' }}
                                    tickFormatter={(value) => value.toLocaleString()} 
                                />
                                <Tooltip 
                                    wrapperClassName='revenue-axis'
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                    formatter={(value, name, props) => {
                                        const nameStr = String(name);
                                        const originalValue = props.payload[`original${nameStr.charAt(0).toUpperCase() + nameStr.slice(1)}`];
                                        return [originalValue.toLocaleString(), name];
                                    }}
                                />
                                <Line type="monotone" dataKey="consultation" stroke="#00a297" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                <Line type="monotone" dataKey="tests" stroke="#00443f" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                <Line type="monotone" dataKey="pharmacy" stroke="#00bb8e" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                <Line type="monotone" dataKey="homecare" stroke="#1b8e50" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            </LineChart>
                        }
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default OverAllStatsGraph;